/**
 * API client for platform services
 *
 * Supports both authentication methods during migration:
 * 1. NEW: Platform tokens (Auth0 → Platform auth service) - for backend API calls
 * 2. LEGACY: Palantir tokens (Auth0 → Palantir via third-party auth) - for OSDK operations
 *
 * Both use the same Auth0 login - no double authentication required.
 */

import { createClient } from "@osdk/client";
import { createPublicOauthClient } from "@osdk/oauth";
import { config } from "./config";
import { authenticatedFetch, authenticatedJson } from "@/auth";

// =============================================================================
// NEW: Platform Auth (Auth0 → Platform tokens)
// Use for backend API calls (will eventually replace all Palantir token usage)
// =============================================================================

/**
 * Make an authenticated fetch request to platform APIs
 * Handles token injection and automatic refresh
 */
export { authenticatedFetch, authenticatedJson };

// =============================================================================
// LEGACY: Palantir Auth (Auth0 → Palantir via their third-party auth integration)
// Used for OSDK operations during migration period
// =============================================================================

const palantirScopes = [
    "api:ontologies-read",
    "api:ontologies-write",
    "api:admin-read",
    "api:admin-write",
];

const palantirAuxiliaryScopes = [
    "api:mediasets-write",
    "api:mediasets-read",
    "api:aip-agents-read",
    "api:aip-agents-write",
    "api:admin-read",
];

const isLocalhost = window.location.toString().includes("localhost:5173");

const palantirRedirectUrl = isLocalhost
    ? "http://localhost:5173/auth/palantir-callback"
    : config.palantirRedirectUrl;

const palantirAuxiliaryRedirectUrl = isLocalhost
    ? "http://localhost:5173/auth/auxiliary-callback"
    : config.palantirAuxiliaryRedirectUrl;

/**
 * Palantir OAuth client - uses Auth0 as IdP via Palantir's third-party auth
 * @deprecated Will be removed after migration - use getPlatformToken() for new code
 */
export const auth = createPublicOauthClient(
    config.palantirClientId,
    config.palantirFoundryUrl,
    palantirRedirectUrl,
    true,
    undefined,
    window.location.toString(),
    palantirScopes,
);

/**
 * Palantir auxiliary OAuth client for additional scopes
 * @deprecated Will be removed after migration
 */
export const auxiliaryAuth = createPublicOauthClient(
    config.palantirAuxiliaryClientId,
    config.palantirFoundryUrl,
    palantirAuxiliaryRedirectUrl,
    true,
    undefined,
    window.location.toString(),
    palantirAuxiliaryScopes,
);

/**
 * Palantir OSDK client for ontology operations
 * @deprecated Will be removed after migration - use backend APIs instead
 */
export const client = createClient(
    config.palantirFoundryUrl,
    config.palantirOntologyRid,
    auth,
);

/**
 * Logout from Palantir
 * @deprecated Use the logout from useAuth() hook instead
 */
export async function logout(): Promise<void> {
    await auth.signOut();
    await auxiliaryAuth.signOut();

    // Redirect to Palantir's OIDC logout endpoint
    window.location.href = `${config.palantirFoundryUrl}/multipass/api/collectors/oidc/logout/83fbbe91-57a2-4608-8aab-2231b5d32e7e/frontchannel`;
}
